import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Stack,
  Text,
  Textarea,
  VStack,
  HStack,
  useColorModeValue,
  SimpleGrid,
  Icon,
  useToast,
  chakra,
  Divider,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import {
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaWhatsapp,
  FaLinkedin,
  FaTwitter,
} from 'react-icons/fa';

const ChakraBox = chakra(motion.div, {
  shouldWrapChildren: true,
});

const ContactCard = ({ icon, title, content, link }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const iconBg = useColorModeValue('brand.50', 'gray.700');

  return (
    <ChakraBox
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Stack
        direction="row"
        align="center"
        bg={cardBg}
        p={6}
        rounded="xl"
        shadow="lg"
        spacing={6}
        _hover={{
          transform: 'translateY(-5px)',
          shadow: '2xl',
        }}
        transition="all 0.3s"
        cursor={link ? 'pointer' : 'default'}
        onClick={() => link && window.open(link, '_blank')}
      >
        <Box
          p={4}
          bg={iconBg}
          rounded="full"
          color="brand.500"
        >
          <Icon as={icon} w={6} h={6} />
        </Box>
        <Stack spacing={1}>
          <Text fontWeight="bold" fontSize="lg">
            {title}
          </Text>
          <Text color={useColorModeValue('gray.600', 'gray.300')}>
            {content}
          </Text>
        </Stack>
      </Stack>
    </ChakraBox>
  );
};

const SocialButton = ({ icon, label, href }) => {
  return (
    <Button
      as="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      size="lg"
      height="64px"
      width="full"
      variant="ghost"
      leftIcon={<Icon as={icon} w={6} h={6} />}
      _hover={{
        bg: useColorModeValue('brand.50', 'gray.700'),
        transform: 'translateY(-2px)',
        shadow: 'md',
      }}
      transition="all 0.3s"
    >
      {label}
    </Button>
  );
};

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const toast = useToast();
  const bgGradient = useColorModeValue(
    'linear(to-r, brand.600, brand.400)',
    'linear(to-r, brand.900, brand.700)'
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    // Add your form submission logic here
    toast({
      title: 'Message Sent!',
      description: "We'll get back to you as soon as possible.",
      status: 'success',
      duration: 5000,
      isClosable: true,
    });
    setFormData({ name: '', email: '', phone: '', message: '' });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box
        bgGradient={bgGradient}
        color="white"
        py={20}
        position="relative"
        overflow="hidden"
      >
        <Container maxW="7xl">
          <ChakraBox
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Stack spacing={6} maxW="xl">
              <Heading
                fontSize={{ base: '3xl', md: '4xl', lg: '5xl' }}
                fontWeight="bold"
                lineHeight="shorter"
              >
                Get in Touch
                <Text
                  as="span"
                  bgGradient="linear(to-r, yellow.200, yellow.100)"
                  bgClip="text"
                  ml={2}
                >
                  With Us
                </Text>
              </Heading>
              <Text fontSize="xl" opacity={0.9}>
                Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
              </Text>
            </Stack>
          </ChakraBox>
        </Container>
      </Box>

      {/* Contact Info Section */}
      <Box py={20}>
        <Container maxW="7xl">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10}>
            {/* Contact Form */}
            <ChakraBox
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <VStack
                as="form"
                spacing={8}
                onSubmit={handleSubmit}
                bg={useColorModeValue('white', 'gray.800')}
                rounded="xl"
                shadow="xl"
                p={8}
              >
                <Heading size="lg">Send us a Message</Heading>
                <FormControl isRequired>
                  <FormLabel>Name</FormLabel>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your name"
                    size="lg"
                    bg={useColorModeValue('gray.50', 'gray.700')}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Email</FormLabel>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your.email@example.com"
                    size="lg"
                    bg={useColorModeValue('gray.50', 'gray.700')}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Phone</FormLabel>
                  <Input
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Your phone number"
                    size="lg"
                    bg={useColorModeValue('gray.50', 'gray.700')}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Message</FormLabel>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Your message"
                    size="lg"
                    bg={useColorModeValue('gray.50', 'gray.700')}
                    rows={5}
                  />
                </FormControl>
                <Button
                  type="submit"
                  colorScheme="brand"
                  size="lg"
                  width="full"
                  _hover={{
                    transform: 'translateY(-2px)',
                    boxShadow: 'lg',
                  }}
                >
                  Send Message
                </Button>
              </VStack>
            </ChakraBox>

            {/* Contact Information */}
            <VStack spacing={8} align="stretch">
              <ContactCard
                icon={FaPhone}
                title="Phone"
                content="+91 123 456 7890"
                link="tel:+911234567890"
              />
              <ContactCard
                icon={FaEnvelope}
                title="Email"
                content="support@budgetbrilliance.com"
                link="mailto:support@budgetbrilliance.com"
              />
              <ContactCard
                icon={FaMapMarkerAlt}
                title="Office"
                content="123 Financial District, Mumbai, Maharashtra 400001"
              />
              <Divider />
              <Heading size="md">Connect With Us</Heading>
              <SimpleGrid columns={2} spacing={4}>
                <SocialButton
                  icon={FaWhatsapp}
                  label="WhatsApp"
                  href="https://wa.me/911234567890"
                />
                <SocialButton
                  icon={FaLinkedin}
                  label="LinkedIn"
                  href="https://linkedin.com/company/budgetbrilliance"
                />
              </SimpleGrid>
            </VStack>
          </SimpleGrid>
        </Container>
      </Box>
    </Box>
  );
};

export default ContactUs;
